import { Button } from '@codegouvfr/react-dsfr/Button';
import {useTextes} from "@common/config";
import {
  EMAIL_PLACEHOLDER,
  FIRST_NAME_PLACEHOLDER,
  JOB_PLACEHOLDER,
  LAST_NAME_PLACEHOLDER,
  PHONE_PLACEHOLDER,
} from '@common/schemas';
import { CheckboxField, Form, TextField } from '@common/ui';
import { isSchemaIncludeAnOther } from '@common/utils';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ZodObject } from 'zod';
import { useAppDispatch } from '../../../hooks/store.hook';
import { useInscriptionState } from '../../../hooks/use-inscription-state.hook';
import {
  resetUtilisateurAdmin2,
  setAdmin1AndRepresentantAreSame,
  setUtilisateurAdmin1,
  setUtilisateurAdmin2,
  setUtilisateurRepresentant,
} from '../../../stores/inscription.slice';
import { StepHeader } from '../components/StepHeader';
import { useStepperInfo } from '../hooks/use-stepper-info.hook';
import {
  step1AsyncFormSchema,
  Step1FormResult,
  step1WithSecondAdminAsyncFormSchema,
  Step1WithSecondAdminFormResult,
} from './schemas/step1-async-form.schema';
import './Step1.css';

export const Step1 = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const t = useTextes('Inscription', 'Etape1Administrateurs');

  const inscription = useInscriptionState();

  const [toggleAddAdmin, setToggleAddAdmin] = useState<boolean>(!!inscription.admin2);
  const [formSchema, setFormSchema] = useState<ZodObject>((toggleAddAdmin) ? step1WithSecondAdminAsyncFormSchema : step1AsyncFormSchema);

  useStepperInfo({ step: 1 });

  useEffect(() => {
    if (toggleAddAdmin) {
      setFormSchema(step1WithSecondAdminAsyncFormSchema);
    } else {
      setFormSchema(step1AsyncFormSchema);
      dispatch(resetUtilisateurAdmin2());
    }
  }, [toggleAddAdmin]);

  const handleSubmit = (result: Step1FormResult) => {
    function isTogglingSecondAdmin(data: Step1FormResult): data is Step1WithSecondAdminFormResult {
      return toggleAddAdmin;
    }

    dispatch(setUtilisateurAdmin1({
      nom: result.firstAdminLastName,
      prenom: result.firstAdminFirstName,
      travail: result.firstAdminJob,
      telephone: result.firstAdminPhoneNumber,
      email: result.firstAdminEmail,
    }));

    if (result.isFirstAdminLegalRepresentative) {
      dispatch(setAdmin1AndRepresentantAreSame(true));
      dispatch(setUtilisateurRepresentant({
        nom: result.firstAdminLastName,
        prenom: result.firstAdminFirstName,
        travail: result.firstAdminJob,
        telephone: result.firstAdminPhoneNumber,
        email: result.firstAdminEmail,
      }));
    }

    if (isTogglingSecondAdmin(result)) {
      dispatch(setUtilisateurAdmin2({
        nom: result.secondAdminLastName,
        prenom: result.secondAdminFirstName,
        travail: result.secondAdminJob,
        telephone: result.secondAdminPhoneNumber,
        email: result.secondAdminEmail,
      }));
    }

    navigate('/inscription/etape-2');
  };

  const values: Step1FormResult = {
    firstAdminLastName: inscription.admin1?.nom ?? '',
    firstAdminFirstName: inscription.admin1?.prenom ?? '',
    firstAdminJob: inscription.admin1?.travail ?? '',
    firstAdminPhoneNumber: inscription.admin1?.telephone ?? '',
    firstAdminEmail: inscription.admin1?.email ?? '',

    isFirstAdminLegalRepresentative: inscription.areAdmin1AndRepresentantSame,

    secondAdminLastName: inscription.admin2?.nom ?? '',
    secondAdminFirstName: inscription.admin2?.prenom ?? '',
    secondAdminJob: inscription.admin2?.travail ?? '',
    secondAdminPhoneNumber: inscription.admin2?.telephone ?? '',
    secondAdminEmail: inscription.admin2?.email ?? '',
  };

  return (
    <div className={'flex flex-col gap-4'}>

      <StepHeader title={'Identité de l\'administrateur'} text={t['INSCR_ADM_TEXT4']} />

      <Form<Step1FormResult>
        schema={formSchema}
        onSubmit={handleSubmit}
        className={'form-step-1 pt-6'}
        values={values}
      >
        <div className={'flex flex-col gap-5'}>
          <TextField name="firstAdminLastName" title="Nom" placeholder={LAST_NAME_PLACEHOLDER} />
          <TextField name="firstAdminFirstName" title="Prénom" placeholder={FIRST_NAME_PLACEHOLDER} />
          <TextField name="firstAdminJob" title="Fonction" placeholder={JOB_PLACEHOLDER} />
          <TextField name="firstAdminPhoneNumber" title="Téléphone" placeholder={PHONE_PLACEHOLDER} />
          <TextField name="firstAdminEmail" title="Courriel" placeholder={EMAIL_PLACEHOLDER} />

          <CheckboxField name="isFirstAdminLegalRepresentative"
                         title="L'identité de l'administrateur est la même que celle du représentant légal"
                         className={'checkbox'} />

          {!toggleAddAdmin && <Button
            iconId={'fr-icon-add-line'}
            priority={'secondary'}
            type={'button'}
            onClick={() => setToggleAddAdmin(true)}
          >
            AJOUTER UN ADMINISTRATEUR
          </Button>
          }
        </div>

        {(toggleAddAdmin && isSchemaIncludeAnOther(formSchema, step1WithSecondAdminAsyncFormSchema)) && (
          <div className={'flex flex-col gap-5 mt-6'}>
            <div className={'flex justify-between'}>
              <span className={'font-bold'}>Administrateur 2:</span>
              <span
                onClick={() => setToggleAddAdmin(false)}
                className={'fr-link font-bold underline underline-offset-4 decoration-2 hover:cursor-pointer'}
              >
                SUPPRIMER L'ADMINISTRATEUR
              </span>
            </div>

            <TextField name="secondAdminLastName" title="Nom" placeholder={LAST_NAME_PLACEHOLDER} />
            <TextField name="secondAdminFirstName" title="Prénom" placeholder={FIRST_NAME_PLACEHOLDER} />
            <TextField name="secondAdminJob" title="Fonction" placeholder={JOB_PLACEHOLDER} />
            <TextField name="secondAdminPhoneNumber" title="Téléphone" placeholder={PHONE_PLACEHOLDER} />
            <TextField name="secondAdminEmail" title="Courriel" placeholder={EMAIL_PLACEHOLDER} />
          </div>
        )}

        <div className={'flex justify-end mt-6 gap-6'}>
          <Button type={'button'} priority={'secondary'} onClick={() => navigate('/')}>PRÉCÉDENT</Button>
          <Button type="submit">VALIDER</Button>
        </div>
      </Form>
    </div>
  );
};
